import {Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild} from '@angular/core';
import {SynopticFile} from '../../../../model/synoptics/synoptic-file';
import {Kpi} from '../../../../model/kpi';
import {DeviceMeasurement} from '../../../../model/device-measurement';
import {AssetMeasurement} from '../../../../model/asset-measurement';
import {
  SynopticConfiguration,
  SynopticConfigurationComponent,
  SynopticElementEvent
} from '../../../../model/synoptics/synoptic-configuration-types';
import {MenuItem} from 'primeng/api';
import {cloneDeep} from 'lodash';
import {ShowPopupArgs} from '../../../../model/synoptics/synoptic-arg-types';
import {Device} from '../../../../model/device';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
  SynopticsFormVisualizationComponent
} from './synoptics-form-visualization/synoptics-form-visualization.component';

@Component({
  selector: 'app-synoptics-form-configuration',
  templateUrl: './synoptics-form-configuration.component.html',
  styleUrls: ['./synoptics-form-configuration.component.scss']
})
export class SynopticsFormConfigurationComponent implements OnInit {
  @ViewChild(SynopticsFormVisualizationComponent) visualizationComponent: SynopticsFormVisualizationComponent;

  @Input() synopticConfiguration: string;
  @Input() synopticInfoForm: FormGroup;
  @Input() synopticFiles: SynopticFile[];
  @Input() deviceMeasurements: DeviceMeasurement[];
  @Input() assetMeasurements: AssetMeasurement[];
  @Input() kpis: Kpi[];
  @Input() devices: Device[];
  @Input() synopticUuid?: string;
  @Output() synopticConfigurationChange: EventEmitter<string>;

  public configuration: SynopticConfiguration;

  public configurationTabs: MenuItem[];
  public configurationTabsActive: MenuItem;

  public uuidSensorMap: Map<string, string>;
  public uuidDeviceMap: Map<string, string>;

  public currentComponent: SynopticConfigurationComponent;

  public showPreview: boolean;
  public componentOptions: MenuItem[];
  public displayComponentDialog: boolean;
  public componentDialogMode: 'create' | 'edit';
  public componentBeingModified: SynopticConfigurationComponent;
  public componentBeingModifiedNameForm: FormControl;
  public componentBeingModifiedOldName: string;

  public screenHeight: number;
  public screenWidth: number;

  constructor() {
    this.synopticConfigurationChange = new EventEmitter<string>();
    this.uuidSensorMap = new Map<string, string>();
    this.uuidDeviceMap = new Map<string, string>();
    this.configurationTabs = [
      { id: 'states', label: 'model.states' },
      { id: 'events', label: 'model.events' },
      { id: 'static', label: 'model.static' }
    ];
    this.componentOptions = [{ label: 'misc.createComponent', icon: 'pi pi-plus' }];
    this.configurationTabsActive = this.configurationTabs[0];
    this.showPreview = false;
    this.componentDialogMode = 'create';
  }

  ngOnInit(): void {
    this.uuidSensorMap = this.buildUuidSensorMap();
    this.uuidDeviceMap = this.buildUuidDeviceMap();

    if (this.synopticConfiguration) {
      this.configuration = JSON.parse(this.synopticConfiguration);
    } else {
      this.configuration = { file: null, name: 'Base', states: [], events: [], statics: [], children: [] };
      setTimeout(() => this.synopticConfigurationChange.emit(JSON.stringify(this.configuration)));
    }

    this.currentComponent = this.configuration;
  }

  public changeComponent(component: SynopticConfigurationComponent): void {
    if (component) {
      this.currentComponent = component;
      this.visualizationComponent?.changeComponent(component);
    }
  }

  public onConfigChange(): void {
    const newConfiguration: string = JSON.stringify(this.configuration);
    this.synopticConfiguration = newConfiguration;
    this.synopticConfigurationChange.emit(newConfiguration);
  }

  private buildUuidSensorMap(): Map<string, string> {
    const result: Map<string, string> = new Map<string, string>();

    this.deviceMeasurements.forEach((measurement: DeviceMeasurement) => result.set(
      `${measurement.device.uuid}/${measurement.uuid}`, `${measurement.name} (${measurement.device.serialNumber})`));

    this.assetMeasurements.forEach((measurement: AssetMeasurement) => result.set(
      `${measurement.asset.uuid}/${measurement.uuid}`, `${measurement.name} (${measurement.asset.alias})`));

    this.kpis.forEach((kpi: Kpi) => result.set(`${kpi.uuid}/null`, kpi.name));

    return result;
  }

  private buildUuidDeviceMap(): Map<string, string> {
    const result: Map<string, string> = new Map<string, string>();
    this.devices.forEach((device: Device) => result.set(device.uuid, device.serialNumber));
    return result;
  }

  public startCreatingComponent(): void {
    this.componentBeingModified = { name: '', file: null, states: [], events: [], statics: [] };
    this.componentBeingModifiedOldName = '';
    this.componentDialogMode = 'create';
    this.displayComponentDialog = true;
    this.initializeComponentNameForm('');
  }

  public startEditingComponent(component: SynopticConfigurationComponent): void {
    this.componentBeingModified = cloneDeep(component);
    this.componentBeingModifiedOldName = component.name;
    this.componentDialogMode = 'edit';
    this.displayComponentDialog = true;
    this.initializeComponentNameForm(component.name);
  }

  public startDuplicatingComponent(component: SynopticConfigurationComponent): void {
    this.componentBeingModified = cloneDeep(component);
    this.componentBeingModifiedOldName = '';
    this.componentDialogMode = 'create';
    this.displayComponentDialog = true;
    this.initializeComponentNameForm(component.name);
  }

  private initializeComponentNameForm(initialValue: string): void {
    this.componentBeingModifiedNameForm = new FormControl(initialValue, [(control: FormControl) => {
      const trimmed = control.value.trim();
      const changed: boolean = this.componentBeingModifiedOldName !== trimmed;
      const exists: boolean = trimmed === this.configuration.name || this.configuration.children.map(c => c.name).includes(trimmed);
      return (changed && exists) ? { componentWithNameExists: { name: trimmed } } : null;
    }, Validators.required]);
  }

  public removeComponent(componentName: string): void {
    this.configuration.children = this.configuration.children.filter(c => c.name !== componentName);
    if (this.currentComponent.name === componentName) {
      this.changeComponent(this.configuration);
    }
    this.removeElementsReferencingComponent(componentName);
    this.onConfigChange();
  }

  private removeElementsReferencingComponent(componentName: string): void {
    this.configuration.events = this.configuration.events.filter(event => this.filterOutPopupRefs(event, componentName));
    for (let child of this.configuration.children) {
      child.events = child.events.filter(event => this.filterOutPopupRefs(event, componentName));
    }
  }

  filterOutPopupRefs(event: SynopticElementEvent, componentName: string): boolean {
    if (event.action.type === 'showPopup') {
      const args: ShowPopupArgs = event.action.args as ShowPopupArgs;
      if (args.component === componentName) {
        return false;
      }
    }
    return true;
  }

  public applyComponentChanges(): void {
    this.componentBeingModified.name = this.componentBeingModifiedNameForm.value.trim();

    if (this.componentDialogMode === 'create') {
      this.configuration.children.push(this.componentBeingModified);
      this.changeComponent(this.componentBeingModified);

    } else if (this.componentDialogMode === 'edit') {
      if (this.configuration.name === this.componentBeingModifiedOldName) {
        this.applyEditableFields(this.componentBeingModified, this.configuration);
      } else {
        const found: SynopticConfigurationComponent = this.configuration.children.find(c => c.name === this.componentBeingModifiedOldName);
        if (found) {
          this.applyEditableFields(this.componentBeingModified, found);
        }
      }
    }

    this.componentBeingModified = null;
    this.componentBeingModifiedOldName = null;
    this.onConfigChange();
  }

  private applyEditableFields(source: SynopticConfigurationComponent, target: SynopticConfigurationComponent): void {
    target.name = source.name;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?: any): void {
    this.screenHeight = window.innerHeight;
    this.screenWidth = window.innerWidth;
  }

}
